import { useEffect, useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DeleteOutlineRounded as DeleteIcon, EditRounded as EditIcon } from '@material-ui/icons';

import { TaxWithUsage } from '../../types/tax.type';
import { formatTaxUsage } from '../../utils/tax-usage.util';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';

type TaxesTableHeader = {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
};

type Props = {
  taxes: TaxWithUsage[];
  isLoading: boolean;
  onEdit: (tax: TaxWithUsage) => void;
  onDelete: (tax: TaxWithUsage) => void;
};

const TABLE_HEADERS: TaxesTableHeader[] = [
  { key: 'name', label: 'Nom', minWidth: 200, align: 'left' },
  { key: 'amount', label: 'Taux', minWidth: 80, align: 'left' },
  { key: 'usage', label: 'Utilisée par', minWidth: 160, align: 'left' },
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 90, align: 'right' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

const useStyles = makeStyles(() => ({
  headerCell: {
    fontWeight: 'bold',
  },
  emptyCell: {
    color: '#757575',
    textAlign: 'center',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.25rem',
  },
}));

export function TaxesTable({ taxes, isLoading, onEdit, onDelete }: Props) {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const lastPage = Math.max(0, Math.ceil(taxes.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);

  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);
  const visibleTaxes = taxes.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '65svh' }}>
        <Table size="small" stickyHeader aria-label="Liste des taxes">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  className={classes.headerCell}
                  style={{ minWidth: header.minWidth }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {taxes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className={classes.emptyCell}>
                    Aucune taxe : créez-en une depuis le formulaire ci-contre.
                  </TableCell>
                </TableRow>
              )}
              {visibleTaxes.map((tax) => {
                const isUsed = tax.usageCount > 0;
                const deleteButton = (
                  <IconButton size="small" aria-label="Supprimer" disabled={isUsed} onClick={() => onDelete(tax)}>
                    <DeleteIcon />
                  </IconButton>
                );

                return (
                  <TableRow hover key={tax.id}>
                    <TableCell>{tax.name}</TableCell>
                    <TableCell>{`${tax.amount} %`}</TableCell>
                    <TableCell>{formatTaxUsage(tax.usageCount)}</TableCell>
                    <TableCell align="right">
                      <div className={classes.rowActions}>
                        <IconButton size="small" aria-label="Modifier" onClick={() => onEdit(tax)}>
                          <EditIcon />
                        </IconButton>
                        {isUsed ? (
                          <Tooltip title={`Utilisée par ${formatTaxUsage(tax.usageCount)}`}>
                            <span>{deleteButton}</span>
                          </Tooltip>
                        ) : (
                          deleteButton
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={taxes.length}
        page={currentPage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => handleRowsPerPageChange(Number(event.target.value))}
      />
    </Paper>
  );
}
